import React, { useState, KeyboardEvent, useMemo, useCallback, useEffect, useRef } from 'react';
import { TextField, IconButton, Stack } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { ReactComponent as AIBubbleIcon } from '../../../assets/assistant/ai-bubble.svg';
import './AssistantChatInput.scss';
import { ArrowUpward, Stop } from '@mui/icons-material';
import { useAssistantChat } from '../../../data-access';
import { useSlashCommand } from '../hooks/useSlashCommand';
import { SlashCommandMenu } from './SlashCommandMenu';
import type { SlashCommandShortcut } from './constants';
import { useTranslation } from 'react-i18next';

interface ChatInputProps {
  disabled?: boolean;
  placeholder?: string;
  focusCounter: number;
  setFocusCounter: (focusCounter: number) => void;
  prefillText?: string;
  prefillVersion?: number;
  clearVersion?: number;
  hideLeadingIcon?: boolean;
  onShortcutSelect?: (shortcut: SlashCommandShortcut) => void;
}

const AssistantChatInput: React.FC<ChatInputProps> = ({
  disabled = false,
  placeholder,
  focusCounter,
  setFocusCounter,
  prefillText,
  prefillVersion,
  clearVersion,
  hideLeadingIcon = false,
  onShortcutSelect,
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const {
    messages,
    startGeneralChat,
    currentSessionType,
    sendRecommendation,
    sendMessage,
    isStreaming,
    loading,
    stopChat,
    isAllPlanLoading,
  } = useAssistantChat();
  // Generation keeps running after the browser disconnects, so the send
  // button becomes stop and cancels that job.
  const showStop = isStreaming || loading || isAllPlanLoading;
  const inputRef = useRef<HTMLInputElement>(null);

  const clearMessage = useCallback(() => setMessage(''), []);

  const {
    showMenu,
    filteredShortcuts,
    selectedIndex,
    selectShortcut,
    resetSelection,
    handleKeyDown: handleSlashKeyDown,
  } = useSlashCommand({
    message,
    onSelect: onShortcutSelect ?? (() => {}),
    onClearMessage: clearMessage,
  });

  const isFirstMessage = useMemo(
    () => messages.length === 1 && messages[0].role === 'assistant' && messages[0].start,
    [messages]
  );
  const memoizedHandleSendMessage = useCallback(
    (text: string) => {
      if (currentSessionType === 'recommendation') {
        sendRecommendation(text);
      } else {
        sendMessage(text).then(() => {
          setFocusCounter(focusCounter + 1);
        });
      }
    },
    [sendMessage, currentSessionType, sendRecommendation]
  );
  const handleSend = (text: string) => {
    if (message.trim() && !disabled) {
      setMessage('');
      const newText = text.trim();
      if (!newText) {
        return;
      }
      if (isFirstMessage) {
        startGeneralChat(newText);
      } else {
        memoizedHandleSendMessage(newText);
      }
      window.dispatchEvent(new CustomEvent('ai-chat:message-sent'));
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (handleSlashKeyDown(event)) {
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend(message);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    resetSelection();
  };

  useEffect(() => {
    if (focusCounter > 0 && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [focusCounter]);

  useEffect(() => {
    if (prefillText !== undefined && prefillVersion !== undefined) {
      setMessage(prefillText);
    }
  }, [prefillText, prefillVersion]);

  useEffect(() => {
    if (clearVersion !== undefined) {
      setMessage('');
    }
  }, [clearVersion]);

  return (
    <Stack
      gap={1}
      alignSelf={'flex-end'}
      direction="row"
      alignItems="center"
      className={clsx('assistant-chat-input', {
        'assistant-chat-input--idle-pulse': !isFocused && !disabled && !message.trim(),
      })}
      data-testid="ai-chat-input"
      position="relative"
    >
      {!hideLeadingIcon && <AIBubbleIcon className="assistant-chat-input__ai-bubble-icon" />}
      <AnimatePresence>
        {showMenu && (
          <SlashCommandMenu
            shortcuts={filteredShortcuts}
            selectedIndex={selectedIndex}
            onSelect={selectShortcut}
          />
        )}
      </AnimatePresence>
      <TextField
        value={message}
        inputRef={inputRef}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder ?? t('task.ai.input.typeMessage')}
        multiline
        autoFocus={isFirstMessage || focusCounter > 0}
        maxRows={3}
        variant="outlined"
        className="assistant-chat-input__field"
        InputProps={{
          endAdornment: (
            <IconButton
              onClick={() => {
                if (showStop) {
                  stopChat();
                  return;
                }
                handleSend(message);
              }}
              disabled={showStop ? false : !message.trim() || disabled}
              aria-label={showStop ? t('task.ai.input.stop') : t('task.ai.input.send')}
              data-testid={showStop ? 'ai-chat-stop' : 'ai-chat-send'}
              className="chat-input__send-button"
            >
              {showStop ? (
                <Stop className="chat-input__send-button__icon" />
              ) : (
                <ArrowUpward className="chat-input__send-button__icon" />
              )}
            </IconButton>
          ),
        }}
      />
    </Stack>
  );
};

export default AssistantChatInput;
